import React, { useState } from 'react';
import { ArrowPathIcon, PlayIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import SpeakersList from './SpeakersList';
import ExportOptions from './ExportOptions';
import AddSpeakerModal from '../Modals/AddSpeakerModal';
import RenameSpeakerModal from '../Modals/RenameSpeakerModal';
import DeleteSpeakerModal from '../Modals/DeleteSpeakerModal';

/**
 * Main Sidebar Component
 *
 * Left sidebar with speakers and export options
 */
const Sidebar = ({
  sessionId,
  speakers,
  onSelectSpeaker,
  onAddSpeaker,
  onRenameSpeaker,
  onDeleteSpeaker,
  onExport,
  onReplaceAll,
  onPlayFromSelection,
  onAssignSelectionToSpeaker,
  selectedText,
  selection,
  className = '',
}) => {
  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);

  /**
   * Handle add speaker
   */
  const handleAddSpeaker = (speakerName) => {
    const result = onAddSpeaker?.(speakerName);
    
    if (result) {
      if (result.success) {
        alert(result.message || 'Speaker added successfully!');
      } else {
        alert('Error: ' + (result.error || 'Failed to add speaker'));
      }
    }
  };

  /**
   * Handle rename speaker
   */
  const handleRenameSpeaker = async (oldName, newName) => {
    await onRenameSpeaker?.(oldName, newName);
  };

  /**
   * Handle delete speaker
   */
  const handleDeleteSpeaker = async (speakerName, mergeToSpeaker) => {
    await onDeleteSpeaker?.(speakerName, mergeToSpeaker);
  };

  return (
    <div className={`bg-white border-r border-gray-200 p-4 pb-8 space-y-6 ${className}`}>
      {/* Text Operations */}
      {(onReplaceAll || onPlayFromSelection || onAssignSelectionToSpeaker) && (
        <div className="pb-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
            Text Operations
          </h3>
          <div className="space-y-2">
            {/* Play Button */}
            {onPlayFromSelection && (
              <button
                onClick={() => {
                  if (selection && selection.range) {
                    onPlayFromSelection(selection);
                  }
                }}
                disabled={!selection || !selection.range}
                className="w-full px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium text-sm"
                title={selection?.text ? `Play from selected word: "${selection.text}"` : 'Select text first to play from that position'}
              >
                <PlayIcon className="h-5 w-5" />
                Play
              </button>
            )}
            {/* Replace All Button */}
            {onReplaceAll && (
              <button
                onClick={() => {
                  if (selectedText) {
                    onReplaceAll(selectedText);
                  } else {
                    const text = prompt('Enter text to replace:');
                    if (text) {
                      onReplaceAll(text);
                    }
                  }
                }}
                disabled={!selectedText}
                className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium text-sm"
                title={selectedText ? `Replace all occurrences of "${selectedText}"` : 'Select text first or enter text to replace'}
              >
                <ArrowPathIcon className="h-5 w-5" />
                Replace All
              </button>
            )}
            {/* Assign to Speaker Button */}
            {onAssignSelectionToSpeaker && (
              <button
                onClick={() => {
                  if (selection && selection.range && selection.text) {
                    onAssignSelectionToSpeaker(selection);
                  }
                }}
                disabled={!selection || !selection.range || !selection.text}
                className="w-full px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium text-sm"
                title={selection?.text ? `Assign selected text to a speaker` : 'Select text first to assign to a speaker'}
              >
                <UserPlusIcon className="h-5 w-5" />
                Assign to speaker
              </button>
            )}
          </div>
          {selectedText && (
            <p className="text-xs text-gray-500 mt-2 text-center truncate">
              Selected: "{selectedText}"
            </p>
          )}
        </div>
      )}

      {/* Speakers List */}
      <div className="pb-4 border-b border-gray-200">
        <SpeakersList
          speakers={speakers}
          onSelectSpeaker={onSelectSpeaker}
          onAddSpeaker={() => setShowAddModal(true)}
          onRenameSpeaker={(speaker) => {
            setSelectedSpeaker(speaker);
            setShowRenameModal(true);
          }}
          onDeleteSpeaker={(speaker) => {
            setSelectedSpeaker(speaker);
            setShowDeleteModal(true);
          }}
        />
      </div>

      {/* Export Options */}
      <ExportOptions onExport={onExport} />

      {/* Speaker Modals */}
      <AddSpeakerModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddSpeaker}
      />

      <RenameSpeakerModal
        isOpen={showRenameModal}
        onClose={() => {
          setShowRenameModal(false);
          setSelectedSpeaker(null);
        }}
        onRename={handleRenameSpeaker}
        currentName={selectedSpeaker}
      />

      <DeleteSpeakerModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedSpeaker(null);
        }}
        onDelete={handleDeleteSpeaker}
        speakerName={selectedSpeaker}
        availableSpeakers={speakers}
      />

      {/* Keyboard Shortcuts Help */}
      <div className="pt-4 pb-4 border-t border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
          Keyboard Shortcuts
        </h3>
        <div className="space-y-2 text-xs text-gray-600">
          <div className="flex justify-between">
            <span>Split block</span>
            <div className="flex gap-1">
              <kbd className="px-2 py-1 bg-gray-100 rounded">Shift + Enter</kbd>
            </div>
          </div>
          <div className="flex justify-between">
            <span>Merge up</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded">Shift + Backspace</kbd>
          </div>
          <div className="flex justify-between">
            <span>Merge down</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded">Shift + Delete</kbd>
          </div>
          {/* <div className="flex justify-between">
            <span>Undo</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded">Cmd+Z</kbd>
          </div>  */}
          {/* <div className="flex justify-between">
            <span>Redo</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded">Cmd+Shift+Z</kbd>
          </div> */}
          {/* <div className="flex justify-between">
            <span>Save</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded">Cmd+S</kbd>
          </div> */}
          <div className="flex justify-between">
            <span>Search</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded">Ctrl + F</kbd>
          </div>
          <div className="flex justify-between">
            <span>Play/Pause</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded">Shift + Space</kbd>
          </div>
          <div className="flex justify-between">
            <span>Skip -5s</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded">Shift + ←</kbd>
          </div>
          <div className="flex justify-between">
            <span>Skip +5s</span>
            <kbd className="px-2 py-1 bg-gray-100 rounded">Shift + →</kbd>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;


